"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { getConsoleAccess, getBoard, transitionPolicy } from "@/lib/policies/api";
import { StatusChip, LIFECYCLE_COLUMNS } from "../StatusChip";
import { AssignmentModal } from "./AssignmentModal";
import { CollationModal } from "./CollationModal";
import { PolicyJourneyModal } from "./PolicyJourneyModal";
import { BoardPacketView } from "./BoardPacketView";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";

// Guards against transitions that must be gated actions elsewhere in the UI —
// mirrors the design reference's canMove() so an invalid drag gets an
// immediate, specific reason instead of a bare server error.
function canDrag(from: string, to: string): [boolean, string] {
    if (from === to) return [false, ""];
    if (to === "steward_ratified") return [false, "Governance Chair ratification required — use the Assign → Ratify action."];
    if (to === "in_review") return [false, "Steward Flagged is system-set on the steward's first save or on flagging a statement."];
    if (to === "submitted") return [false, "Submitted is the steward's own submit action."];
    // Board-stage moves (ratified/published) go to the server, which enforces
    // flags, route gates, and the per-policy board-involvement branch.
    return [true, ""];
}

type View = "table" | "kanban" | "packet";

function FollowupBadge() {
    return <span id="console-followup" className="text-[9.5px] font-bold tracking-wide bg-[#fde2dd] text-[#ae232d] rounded px-1.5 py-0.5">FOLLOW-UP</span>;
}

const IDLE_BADGE_DAYS = 14;

// Which modal a card click opens, by lifecycle stage: assignment/governance
// form pre-Submitted, the collation triage queue while staff is actively
// working submitted input (Submitted/In Collation — the latter now also
// shows already-applied decisions read-only), and the read-only journey
// (original vs. current text, who changed it) once there's no more triage
// action to take (Applied through Published).
const ASSIGN_STAGES = new Set(["unassigned", "steward_proposed", "steward_ratified", "in_review"]);
const COLLATION_STAGES = new Set(["submitted", "in_collation"]);

function IdleBadge({ days }: { days: number }) {
    return <span title="Days without steward activity — may need a nudge" className="text-[9.5px] font-bold tracking-wide bg-[#fbecd6] text-[#9a6a16] rounded px-1.5 py-0.5">IDLE {days}d</span>;
}

export function PolicyConsolePanel() {
    const [access, setAccess] = useState<boolean | null>(null);
    const [policies, setPolicies] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState<View>("table");
    const [search, setSearch] = useState("");
    const [domainFilter, setDomainFilter] = useState("all");
    const [stateFilter, setStateFilter] = useState("all");
    const [flagFilter, setFlagFilter] = useState("all");
    const [assignTarget, setAssignTarget] = useState<any>(null);
    const [collateTarget, setCollateTarget] = useState<any>(null);
    const [journeyTarget, setJourneyTarget] = useState<any>(null);
    const [dragOverCol, setDragOverCol] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const { replay: replayTour } = useAutoTour("staff.policyConsole", TOURS["staff.policyConsole"]);

    const load = () => {
        setLoading(true);
        setLoadError(null);
        getBoard()
            .then(setPolicies)
            .catch((e) => setLoadError(e.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        getConsoleAccess()
            .then((r) => setAccess(r.is_policy_admin))
            .catch((e) => { console.error("Failed to check policy console access:", e); setAccess(false); setLoadError(e.message); });
    }, []);

    useEffect(() => {
        if (access) load();
    }, [access]);

    const domains = useMemo(() => Array.from(new Set(policies.map((p) => p.id.split("-")[0]))).sort(), [policies]);

    const filtered = policies.filter((p) => {
        if (domainFilter !== "all" && p.id.split("-")[0] !== domainFilter) return false;
        if (stateFilter !== "all" && p.lifecycle_state !== stateFilter) return false;
        if (flagFilter === "flags" && p.unresolved_flag_count === 0) return false;
        if (flagFilter === "followup" && !p.followup) return false;
        if (flagFilter === "none" && p.unresolved_flag_count > 0) return false;
        if (search) {
            const q = search.toLowerCase();
            if (!p.id.toLowerCase().includes(q) && !p.name.toLowerCase().includes(q)) return false;
        }
        return true;
    });

    const stats = [
        { label: "Policy units", value: policies.length, color: "#004d57" },
        { label: "Unassigned", value: policies.filter((p) => p.lifecycle_state === "unassigned").length, color: "#a9c4bb" },
        { label: "Steward Flagged", value: policies.filter((p) => p.lifecycle_state === "in_review").length, color: "#4b8892" },
        { label: "Flags open", value: policies.filter((p) => p.unresolved_flag_count > 0).length, color: "#ef4621" },
        { label: "Follow-up", value: policies.filter((p) => p.followup).length, color: "#ae232d" },
    ];

    const openCard = (p: any) => {
        if (COLLATION_STAGES.has(p.lifecycle_state)) { setCollateTarget(p); return; }
        if (ASSIGN_STAGES.has(p.lifecycle_state)) { setAssignTarget(p); return; }
        setJourneyTarget(p);
    };

    const onDrop = async (targetState: string, policy: any) => {
        setDragOverCol(null);
        const [ok, reason] = canDrag(policy.lifecycle_state, targetState);
        if (!ok) { if (reason) alert(reason); return; }
        try {
            await transitionPolicy(policy.id, targetState);
            load();
        } catch (e: any) {
            alert(e.message);
        }
    };

    if (access === null || loading) {
        return (
            <div className="py-16 flex flex-col items-center justify-center gap-3">
                <div className="w-8 h-8 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                {loadError && <p className="text-xs text-red-600 max-w-sm text-center">{loadError}</p>}
            </div>
        );
    }

    if (!access) {
        return (
            <div className="max-w-xl mx-auto text-center py-16 px-6">
                <div className="text-4xl">🔒</div>
                <h1 className="mt-3.5 mb-1.5 text-[22px] font-bold text-[#15302f]">Policy Console is staff-only</h1>
                <p className="text-[#5f7173] text-sm leading-relaxed">
                    This board is visible to staff accounts only. Contact an administrator if you believe you should have staff access.
                </p>
                {loadError && <p className="text-xs text-red-500 mt-3">{loadError}</p>}
            </div>
        );
    }

    if (loadError) {
        return <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{loadError}</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <h1 className="text-[22px] font-bold text-[#15302f] tracking-tight">All-policy status board</h1>
                    <p className="mt-1.5 text-[#5f7173] text-[13.5px] max-w-xl">Every policy unit in the Draft → Proposed → Ratified → Published workflow. Assign and ratify a steward, monitor progress in table or Kanban, and clear flags before Board ratification.</p>
                </div>
                <div className="flex items-center gap-4">
                    <TourButton onClick={replayTour} />
                    <div id="console-view-toggle" className="flex bg-[#e8edeb] rounded-[9px] p-[3px]">
                        <button
                            onClick={() => setView("table")}
                            style={view === "table" ? { background: "#fff", color: "#004d57", boxShadow: "0 1px 3px rgba(0,40,46,0.12)" } : { background: "transparent", color: "#5f7173" }}
                            className="text-xs font-bold rounded-[7px] px-4 py-1.5"
                        >
                            ▦ Table
                        </button>
                        <button
                            onClick={() => setView("kanban")}
                            style={view === "kanban" ? { background: "#fff", color: "#004d57", boxShadow: "0 1px 3px rgba(0,40,46,0.12)" } : { background: "transparent", color: "#5f7173" }}
                            className="text-xs font-bold rounded-[7px] px-4 py-1.5"
                        >
                            ▤ Kanban
                        </button>
                        <button
                            onClick={() => setView("packet")}
                            style={view === "packet" ? { background: "#fff", color: "#004d57", boxShadow: "0 1px 3px rgba(0,40,46,0.12)" } : { background: "transparent", color: "#5f7173" }}
                            className="text-xs font-bold rounded-[7px] px-4 py-1.5"
                        >
                            ☰ Board Packet
                        </button>
                    </div>
                </div>
            </div>

            <div id="console-stats" className="flex gap-3.5 flex-wrap">
                {stats.map((s) => (
                    <div key={s.label} style={{ borderLeft: `4px solid ${s.color}` }} className="bg-white border border-gray-200 rounded-xl px-4.5 py-3.5 min-w-[120px]" >
                        <div className="text-[27px] font-bold text-[#15302f] leading-none">{s.value}</div>
                        <div className="text-[11.5px] text-[#5f7173] mt-1.5">{s.label}</div>
                    </div>
                ))}
            </div>

            {view === "table" ? (
                <>
                    <div className="flex gap-3 flex-wrap items-center bg-white border border-gray-200 rounded-xl p-3.5">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search policy id or name…"
                            className="flex-1 min-w-[200px] border border-[#d9dedb] rounded-lg px-3 py-2 text-[13px] text-[#15302f] bg-[#fafbfa] outline-none focus:border-[#4b8892]"
                        />
                        <select value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} className="border border-[#d9dedb] rounded-lg px-3 py-2 text-[13px] bg-[#fafbfa] text-[#15302f]">
                            <option value="all">All domains</option>
                            {domains.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="border border-[#d9dedb] rounded-lg px-3 py-2 text-[13px] bg-[#fafbfa] text-[#15302f]">
                            <option value="all">All states</option>
                            {LIFECYCLE_COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                        </select>
                        <select value={flagFilter} onChange={(e) => setFlagFilter(e.target.value)} className="border border-[#d9dedb] rounded-lg px-3 py-2 text-[13px] bg-[#fafbfa] text-[#15302f]">
                            <option value="all">All flags</option>
                            <option value="flags">⚠ Unresolved flags</option>
                            <option value="followup">Follow-up</option>
                            <option value="none">No flags</option>
                        </select>
                        <span className="text-xs text-[#88a39b] px-1">{filtered.length} shown</span>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
                        <div className="grid grid-cols-[2.7fr_1.5fr_1.5fr_0.7fr_1.5fr_0.9fr_1fr] gap-0 px-4.5 py-2.5 bg-[#f4f6f5] border-b border-gray-200 text-[10.5px] tracking-wide text-[#7c8e90] font-bold min-w-[900px]">
                            <div>POLICY</div><div>OWNER GROUP</div><div>STEWARD</div><div className="text-center">OQ</div><div>STATE</div><div>FLAGS</div><div className="text-right">ACTION</div>
                        </div>
                        {filtered.map((p, i) => (
                            <div key={p.id} id={i === 0 ? "console-row0" : undefined} className="grid grid-cols-[2.7fr_1.5fr_1.5fr_0.7fr_1.5fr_0.9fr_1fr] gap-0 px-4.5 py-3.5 border-b border-[#eef1ef] min-w-[900px] items-center">
                                <div className="cursor-pointer" onClick={() => openCard(p)}>
                                    <div className="font-bold text-[13.5px] text-[#004d57] flex items-center gap-2">
                                        {p.id}
                                        {p.followup && <FollowupBadge />}
                                        {p.idle_days >= IDLE_BADGE_DAYS && <IdleBadge days={p.idle_days} />}
                                    </div>
                                    <div className="text-[12.5px] text-[#3a4d4f] mt-0.5">{p.name}</div>
                                </div>
                                <div className="text-xs text-[#5f7173]">{p.proposed_owner_group}</div>
                                <div className="text-xs" style={{ color: p.assignment?.steward_name ? "#3a4d4f" : "#b5651d" }}>
                                    {p.assignment?.steward_name || "— unassigned —"}
                                    {p.assignment?.state === "proposed" && <span className="block text-[10px] font-bold text-[#9a6a16]">(CEO to ratify)</span>}
                                </div>
                                <div className="text-center text-[13px] font-bold text-[#3a4d4f]">{p.open_question_count}</div>
                                <div><StatusChip state={p.lifecycle_state} /></div>
                                <div>
                                    {p.unresolved_flag_count > 0 && (
                                        <span className="text-[9.5px] font-bold rounded px-1.5 py-0.5 bg-[#f9d2d3] text-[#ae232d]">⚠ {p.unresolved_flag_count}</span>
                                    )}
                                </div>
                                <div className="text-right space-x-2 whitespace-nowrap">
                                    <Link href={`/dashboard/policies/workspace/${p.id}`} className="inline-block border border-[#d9dedb] bg-white text-[#3a6b78] text-[11.5px] font-bold rounded-lg px-2.5 py-1.5 no-underline" title="View / edit this policy (staff)">Open</Link>
                                    <button onClick={() => setAssignTarget(p)} className="border border-[#d9dedb] bg-white text-[#3a6b78] text-[11.5px] font-bold rounded-lg px-2.5 py-1.5">Assign</button>
                                    <button onClick={() => setCollateTarget(p)} className="border-none bg-[#004d57] text-white text-[11.5px] font-bold rounded-lg px-3 py-1.5">Collate</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : view === "kanban" ? (
                <>
                    <div className="text-xs text-[#88a39b]">Columns are the lifecycle state machine. Drag a card to advance it — gated transitions (Governance Chair ratify, steward submit, flag/route/board-involvement blocks) are refused with a reason.</div>
                    <div className="flex gap-3.5 overflow-x-auto pb-4">
                        {LIFECYCLE_COLUMNS.map((col) => {
                            const cards = filtered.filter((p) => p.lifecycle_state === col.id);
                            return (
                                <div
                                    key={col.id}
                                    onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
                                    onDragLeave={() => setDragOverCol(null)}
                                    onDrop={(e) => {
                                        const id = e.dataTransfer.getData("text/plain");
                                        const policy = policies.find((p) => p.id === id);
                                        if (policy) onDrop(col.id, policy);
                                    }}
                                    style={{ background: dragOverCol === col.id ? "#e2ede8" : "#eef1f0" }}
                                    className="flex-none w-[248px] rounded-xl p-2.5 flex flex-col gap-2.5 min-h-[120px]"
                                >
                                    <div className="flex items-center justify-between px-1 py-0.5">
                                        <span className="text-[11.5px] font-bold text-[#3a4d4f]">{col.label}</span>
                                        <span className="text-[11px] font-bold text-[#88a39b] bg-white rounded-full px-2 py-0.5">{cards.length}</span>
                                    </div>
                                    {cards.map((p) => (
                                        <div
                                            key={p.id}
                                            draggable
                                            onDragStart={(e) => e.dataTransfer.setData("text/plain", p.id)}
                                            onClick={() => openCard(p)}
                                            style={{ borderLeft: `3px solid ${p.unresolved_flag_count ? "#ef4621" : "#cdd9d4"}` }}
                                            className="bg-white border border-gray-200 rounded-[9px] p-2.5 cursor-grab shadow-sm"
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-bold text-[12.5px] text-[#004d57]">{p.id}</span>
                                                {p.unresolved_flag_count > 0 && <span className="text-[11px]">⚠</span>}
                                                {p.followup && <span className="text-[9px] font-bold bg-[#fde2dd] text-[#ae232d] rounded px-1 py-0.5 ml-auto">FU</span>}
                                                {p.idle_days >= IDLE_BADGE_DAYS && <span className="text-[9px] font-bold bg-[#fbecd6] text-[#9a6a16] rounded px-1 py-0.5">{p.idle_days}d</span>}
                                            </div>
                                            <div className="text-[11.5px] text-[#3a4d4f] mt-1 leading-snug truncate">{p.name}</div>
                                            <div className="text-[10.5px] text-[#88a39b] mt-1.5">{p.assignment?.steward_name?.split(" ")[0] || "unassigned"} · {p.id.split("-")[0]} · {p.open_question_count} OQ</div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <BoardPacketView />
            )}

            {assignTarget && (
                <AssignmentModal
                    policy={assignTarget}
                    onClose={() => setAssignTarget(null)}
                    onDone={() => { setAssignTarget(null); load(); }}
                />
            )}
            {collateTarget && (
                <CollationModal policy={collateTarget} onClose={() => setCollateTarget(null)} onApplied={load} />
            )}
            {journeyTarget && (
                <PolicyJourneyModal
                    policy={journeyTarget}
                    onClose={() => setJourneyTarget(null)}
                    onManageGovernance={() => { setAssignTarget(journeyTarget); setJourneyTarget(null); }}
                />
            )}
        </div>
    );
}
